import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Stack from "@mui/material/Stack";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import Div from "@jumbo/shared/Div";
import { getProductList } from "app/redux/slice/productSlice";
import {
  bulkUpdatePartyRatePrice,
  getPartyRateList,
} from "app/redux/slice/partyRateSlice";
import { PARTY_RATE } from "app/routes/constant";
import { userType } from "app/helpers/userType";

// Edit the price several vendors pay for ONE product, side by side, and save
// them in a single click.
//
// The rows come from the same list endpoint the Party Rate screen uses, filtered
// by product - so a vendor appears here only if they already have a rate on this
// product. Adding a brand-new rate is still the "Add Party Rate" form's job;
// this screen only ever changes a price that already exists.
const ProductWisePrice = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const productData = useSelector((state) => state?.product);

  const [product, setProduct] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Keyed by rate id. Holds what the operator typed, which is a string while
  // they are typing - the saved price stays on the row itself.
  const [edited, setEdited] = useState({});

  // Same check the Party Rate list uses - userType() reads the stored user and
  // reports whether they are the master admin, who always has every permission.
  const { isMA } = userType();
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );
  const canUpdate = useMemo(
    () =>
      isMA ||
      Boolean(permissionData?.some((p) => p?.name === "update-supplier-rate")),
    [isMA, permissionData],
  );

  useEffect(() => {
    dispatch(getProductList({ field: "name", sort: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A-Z on the vendor name, so a long list is easy to scan and a vendor is easy
  // to find. Sorted here rather than by the API: that list sorts before it looks
  // the supplier up, so `supplier.name` does not exist yet at that point. This
  // screen loads one product's vendors in a single page, so sorting the array is
  // both correct and cheap.
  //
  // localeCompare with numeric handles names like "Vendor 2" and "Vendor 10" in
  // the order a person expects, and sensitivity 'base' keeps case out of it.
  const sortByVendor = (list) =>
    [...list].sort((a, b) =>
      String(a?.supplier?.name || '').localeCompare(
        String(b?.supplier?.name || ''),
        undefined,
        { numeric: true, sensitivity: 'base' },
      ),
    );

  const loadRates = (selected) => {
    if (!selected?._id) {
      setRows([]);
      setEdited({});
      return;
    }
    setLoading(true);
    setEdited({});
    dispatch(
      // No page/perPage: this screen shows every vendor priced on the product,
      // because the point is to see them together.
      getPartyRateList({ productId: selected._id, field: "createdAt", sort: -1 }),
    )
      .unwrap()
      .then((res) => {
        setRows(sortByVendor(res?.data?.data?.supplierRateList ?? []));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // A row counts as changed only when the typed value differs from the saved
  // one, so re-typing the same number does not mark it dirty.
  const changedRows = useMemo(
    () =>
      rows.filter((row) => {
        const typed = edited[row._id];
        if (typed === undefined) return false;
        if (String(typed).trim() === "") return false;
        return Number(typed) !== Number(row.price ?? 0);
      }),
    [rows, edited],
  );

  const hasEmpty = useMemo(
    () =>
      Object.values(edited).some((v) => v !== undefined && String(v).trim() === ""),
    [edited],
  );

  const valueFor = (row) =>
    edited[row._id] !== undefined ? edited[row._id] : (row.price ?? 0);

  const handleChange = (row, value) => {
    setEdited((prev) => ({ ...prev, [row._id]: value }));
  };

  const resetAll = () => setEdited({});

  const handleUpdate = async () => {
    if (!changedRows.length) return;
    setSaving(true);
    const res = await dispatch(
      bulkUpdatePartyRatePrice({
        updates: changedRows.map((row) => ({
          id: row._id,
          price: Number(edited[row._id]),
        })),
      }),
    ).unwrap();
    setSaving(false);

    if (res?.status === 200) {
      enqueueSnackbar(res.data?.responseMessage, { variant: "success" });
      // Reload rather than patching state by hand, so what is on screen is what
      // the server actually stored.
      loadRates(product);
    } else {
      enqueueSnackbar(
        res?.data?.responseMessage || "Could not update the prices",
        { variant: "error" },
      );
    }
  };

  return (
    <>
      <Card sx={{ display: "flex", mb: 3 }}>
        <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
          <CardContent>
            <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
              <Grid item xs={12} sm={8}>
                <JumboAutoComplete
                  options={productData?.list ?? []}
                  label="Select Product"
                  size="small"
                  getOptionLabel={(option) => option?.name || ""}
                  value={product}
                  name="product"
                  onChange={(evt, value) => {
                    setProduct(value);
                    loadRates(value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(PARTY_RATE)}
                >
                  Back to Party Rate
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Div>
      </Card>

      <Card>
        <CardContent>
          <Stack
            justifyContent="space-between"
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 3 }}
            spacing={1}
          >
            <div>
              <Typography variant={"h2"} mb={0}>
                Product Wise Price
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {product
                  ? `${rows.length} vendor(s) priced on ${product.name}`
                  : "Pick a product to see every vendor priced on it."}
              </Typography>
            </div>
            <Stack direction="row" spacing={1} alignItems="center">
              {changedRows.length > 0 && (
                <>
                  <Chip
                    size="small"
                    color="warning"
                    label={`${changedRows.length} unsaved`}
                  />
                  <Tooltip title="Undo all changes">
                    <Button
                      size="small"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={resetAll}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                </>
              )}
              <LoadingButton
                variant="contained"
                loading={saving}
                loadingIndicator={<CircularProgress size={20} />}
                disabled={!canUpdate || !changedRows.length || hasEmpty}
                onClick={handleUpdate}
              >
                Update {changedRows.length > 0 ? `(${changedRows.length})` : ""}
              </LoadingButton>
            </Stack>
          </Stack>

          {!canUpdate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You do not have permission to change party rates - prices are shown
              read-only.
            </Alert>
          )}
          {hasEmpty && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A price cannot be left blank. Enter a value, or press Reset to undo.
            </Alert>
          )}

          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Belongs To</TableCell>
                  <TableCell>Mobile No</TableCell>
                  <TableCell>GST</TableCell>
                  <TableCell align="right" sx={{ minWidth: 170 }}>
                    Price
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : !product ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      Select a product above to begin.
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No vendor has a rate on this product yet. Use Add Party
                      Rate to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const isChanged = changedRows.some((c) => c._id === row._id);
                    const typed = edited[row._id];
                    const isBlank =
                      typed !== undefined && String(typed).trim() === "";
                    return (
                      <TableRow
                        key={row._id}
                        sx={isChanged ? { bgcolor: "warning.light" } : undefined}
                      >
                        <TableCell>{row.supplier?.name || "—"}</TableCell>
                        <TableCell>{row.supplier?.user?.name || "—"}</TableCell>
                        <TableCell>{row.mobileNumber || "—"}</TableCell>
                        <TableCell>{row.gst || "—"}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            disabled={!canUpdate}
                            value={valueFor(row)}
                            error={isBlank}
                            // Dropping focus stops a scroll past the table from
                            // silently changing a price.
                            onWheel={(e) => e.target.blur()}
                            onChange={(e) => handleChange(row, e.target.value)}
                            inputProps={{
                              min: 0,
                              step: "any",
                              style: { textAlign: "right" },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">₹</InputAdornment>
                              ),
                            }}
                            sx={{ width: 160 }}
                            helperText={
                              isChanged ? `was ₹${row.price ?? 0}` : " "
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {changedRows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning">
                {changedRows.length} price(s) changed but not saved yet. Press
                Update to apply them.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ProductWisePrice;
